/**
 * The physiological layer: turning a green-channel trace into a reading, or
 * into an honest refusal.
 *
 * The single most important behaviour in this file is that `ScanSession` can
 * finish with `locked: false`. A conventional estimator takes a fixed window,
 * finds the largest spectral peak and reports it as a heart rate — which on
 * dark skin means reporting noise as a vital sign, precisely, and with no
 * indication that anything went wrong. This one keeps integrating until the
 * peak is genuinely prominent, and if that never happens it says so.
 */

import { bandPeak, detrend, harmonicSupport, perfusionIndex, powerSpectrum } from './dsp.js';

export const ENGINE_VERSION = '1.0.0';

export const DEFAULTS = Object.freeze({
  fs: 30,
  /** 42–240 bpm. Anything outside this is not a resting human pulse. */
  pulseBand: [0.7, 4.0],
  /** 6–30 breaths per minute. */
  respiratoryBand: [0.1, 0.5],
  padFactor: 4,
  /** Competing peaks are ignored within this distance of the candidate. */
  excludeHz: 0.25,
  /** Shortest window we will even consider — 8.5 s at 30 fps. */
  minSamples: 256,
  /** Give up here — 40 s at 30 fps. */
  maxSamples: 1200,
  /** Re-evaluate every second. */
  stepSamples: 30,
  /**
   * How far the peak must stand above the best competitor before we believe it.
   * Tuned so that a lock is almost always correct rather than so that locks are
   * frequent: see test/estimator.test.js, which asserts both halves.
   */
  prominenceThreshold: 3.0,
  /**
   * How far the second harmonic must stand above the band's noise floor.
   * A real pulse carries energy at 2f; noise that happens to peak does not.
   */
  harmonicThreshold: 1.5,
  /** Two consecutive estimates must agree within this to count as settled. */
  stabilityBpm: 1.5,
});

/**
 * Merge caller options over the defaults, ignoring keys explicitly set to
 * `undefined`. Spreading an options object straight over the defaults silently
 * replaces a threshold with `undefined`, and every comparison against it then
 * returns false — which reads as "the scanner never locks" rather than as a
 * type error. Caught exactly that way once; never again.
 */
export function resolveConfig(opts = {}) {
  const cfg = { ...DEFAULTS };
  for (const [k, v] of Object.entries(opts)) {
    if (v !== undefined) cfg[k] = v;
  }
  return cfg;
}

/**
 * Analyse a complete buffer.
 * @returns {{bpm:number, prominence:number, snr:number, freq:number}|null}
 */
export function analyze(samples, opts = {}) {
  const cfg = resolveConfig(opts);
  if (samples.length < 32) return null;
  const spec = powerSpectrum(samples, { fs: cfg.fs, padFactor: cfg.padFactor });
  const peak = bandPeak(spec, cfg.pulseBand[0], cfg.pulseBand[1], { excludeHz: cfg.excludeHz });
  if (!peak) return null;
  return {
    bpm: peak.freq * 60,
    freq: peak.freq,
    prominence: peak.prominence,
    snr: peak.snr,
    harmonic: harmonicSupport(spec, peak.freq, peak.bandMedian),
  };
}

/**
 * Breathing rate from the same trace. The respiratory component modulates the
 * baseline, so it survives in the low band after detrending.
 */
export function respiratoryRate(samples, opts = {}) {
  const cfg = resolveConfig(opts);
  if (samples.length < cfg.minSamples) return null;
  const spec = powerSpectrum(samples, { fs: cfg.fs, padFactor: cfg.padFactor });
  const peak = bandPeak(spec, cfg.respiratoryBand[0], cfg.respiratoryBand[1], { excludeHz: 0.05 });
  if (!peak) return null;
  return { breathsPerMinute: peak.freq * 60, prominence: peak.prominence };
}

/**
 * Beat-to-beat variability (an SDNN-style index) from time-domain peaks.
 *
 * Reported as an index, not as clinical HRV: camera-derived inter-beat
 * intervals are quantised by the frame rate, which at 30 fps is 33 ms — the
 * same order as the quantity being measured. Useful as a trend, not as a number
 * to compare against a chest strap.
 */
export function variabilityIndexMs(samples, bpm, opts = {}) {
  const cfg = resolveConfig(opts);
  if (!bpm || samples.length < cfg.minSamples) return null;

  const d = detrend(samples);
  const minGap = Math.max(6, Math.floor(((cfg.fs * 60) / bpm) * 0.6));
  const peaks = [];
  for (let i = 1; i < d.length - 1; i++) {
    if (d[i] > d[i - 1] && d[i] >= d[i + 1]) {
      const lastIdx = peaks[peaks.length - 1];
      if (peaks.length && i - lastIdx < minGap) {
        if (d[i] > d[lastIdx]) peaks[peaks.length - 1] = i;
      } else {
        peaks.push(i);
      }
    }
  }
  if (peaks.length < 4) return null;

  const intervals = [];
  for (let i = 1; i < peaks.length; i++) intervals.push(((peaks[i] - peaks[i - 1]) / cfg.fs) * 1000);
  const mean = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  const variance = intervals.reduce((a, b) => a + (b - mean) ** 2, 0) / intervals.length;
  return Math.sqrt(variance);
}

export { perfusionIndex };

export const ScanState = Object.freeze({
  IDLE: 'idle',
  ACQUIRING: 'acquiring',
  LOCKED: 'locked',
  NO_SIGNAL: 'no_signal',
});

/**
 * The scan state machine.
 *
 * Feed it samples; it decides when it has enough. Deliberately reports
 * `conventionalBpm` alongside the verdict — the number a fixed-window
 * estimator would have returned from the very same data — because on dark skin
 * the gap between the two is the entire argument.
 */
export class ScanSession {
  constructor(opts = {}) {
    this.cfg = resolveConfig(opts);
    this.buffer = new Float64Array(this.cfg.maxSamples);
    this.length = 0;
    this.state = ScanState.IDLE;
    this.previousBpm = null;
    this.conventional = null;
    this.latest = null;
    this.result = null;
    this.startedAt = null;
  }

  get seconds() {
    return this.length / this.cfg.fs;
  }

  get progress() {
    return Math.min(1, this.length / this.cfg.maxSamples);
  }

  /** Samples so far, as a view — do not retain across further pushes. */
  view() {
    return this.buffer.subarray(0, this.length);
  }

  /**
   * Push one sample.
   * @returns {ScanState} the state after this sample
   */
  push(value) {
    if (this.state === ScanState.LOCKED || this.state === ScanState.NO_SIGNAL) return this.state;
    if (this.state === ScanState.IDLE) {
      this.state = ScanState.ACQUIRING;
      this.startedAt = Date.now();
    }
    if (!Number.isFinite(value)) return this.state;

    if (this.length < this.cfg.maxSamples) {
      this.buffer[this.length++] = value;
    }

    const { minSamples, stepSamples, maxSamples } = this.cfg;
    if (this.length >= minSamples && (this.length - minSamples) % stepSamples === 0) {
      this.evaluate();
    }
    if (this.state === ScanState.ACQUIRING && this.length >= maxSamples) {
      this.finish(false);
    }
    return this.state;
  }

  evaluate() {
    const samples = this.view();
    const reading = analyze(samples, this.cfg);
    if (!reading) return;
    this.latest = reading;

    // Snapshot what a conventional fixed-window estimator would have said,
    // taken at the first opportunity and never revised.
    if (this.conventional === null && this.length >= this.cfg.minSamples) {
      this.conventional = analyze(samples.slice(0, this.cfg.minSamples), this.cfg);
    }

    const settled =
      this.previousBpm !== null && Math.abs(reading.bpm - this.previousBpm) <= this.cfg.stabilityBpm;

    const prominent = reading.prominence >= this.cfg.prominenceThreshold;
    const harmonicallySupported = reading.harmonic >= this.cfg.harmonicThreshold;

    if (prominent && harmonicallySupported && settled) {
      this.finish(true);
      return;
    }
    this.previousBpm = reading.bpm;
  }

  finish(locked) {
    const samples = this.view();
    const reading = this.latest || analyze(samples, this.cfg);
    this.state = locked ? ScanState.LOCKED : ScanState.NO_SIGNAL;

    const resp = locked ? respiratoryRate(samples, this.cfg) : null;
    this.result = {
      locked,
      engineVersion: ENGINE_VERSION,
      seconds: Number(this.seconds.toFixed(2)),
      samples: this.length,
      heartRateBpm: locked && reading ? Number(reading.bpm.toFixed(1)) : null,
      breathsPerMinute: resp ? Number(resp.breathsPerMinute.toFixed(1)) : null,
      variabilityMs: locked && reading
        ? round1(variabilityIndexMs(samples, reading.bpm, this.cfg))
        : null,
      prominence: reading ? Number(reading.prominence.toFixed(2)) : null,
      perfusionIndex: Number(perfusionIndex(samples).toFixed(5)),
      /** What a conventional fixed-window estimator would have reported. */
      conventionalBpm: this.conventional ? Number(this.conventional.bpm.toFixed(1)) : null,
    };
    return this.result;
  }

  reset() {
    this.length = 0;
    this.state = ScanState.IDLE;
    this.previousBpm = null;
    this.conventional = null;
    this.latest = null;
    this.result = null;
    this.startedAt = null;
  }
}

function round1(v) {
  return v === null || v === undefined ? null : Number(v.toFixed(1));
}

/** Run a complete scan over a pre-generated buffer. Used by tests and sweeps. */
export function runScan(samples, opts = {}) {
  const session = new ScanSession(opts);
  for (let i = 0; i < samples.length; i++) {
    if (session.push(samples[i]) !== ScanState.ACQUIRING) break;
  }
  if (!session.result) session.finish(false);
  return session.result;
}
